export const M4_UNIT_2 = {
  id: "m4-u2-talking-about-routines",
  title: "Unidad 2: Daily Routines",
  topic: "Master talking about daily routines and habits! Learn present simple for everyday actions, work schedules, and pastimes. Essential for describing your lifestyle, work routine, and time management in Canada!",
  iconType: "clock",
  grammarImage: "/grammar/grammar-m4-u2.jpg",
  grammarText: "Present Simple para rutinas: I/You/We/They + base verb | He/She/It + verb+s. Usado para hábitos, horarios y verdades. NO confundir con presente continuo (acciones ahora).",
  
  sets: [
    // SET 1: Basic Routines I/You/We/They
    {
      id: "m4-u2-set1",
      title: "Set 1: Your Daily Routine",
      questions: [
        {
          type: "mc",
          q: "Every morning: 'I ___ up at 6:30am for work.'",
          options: ["wake", "wakes", "waking"],
          a: "wake",
          tip: "I/You/We/They + base verb (no -s)! 'I wake' = simple present for routine."
        },
        {
          type: "input",
          q: "Complete: 'I ___ breakfast at 7am every day.' (eat)",
          a: "eat"
        },
        {
          type: "order",
          q: "Ordena: 'Yo tomo el autobús al trabajo todos los días'",
          words: ["I", "take", "the", "bus", "to", "work", "every", "day."],
          a: "I take the bus to work every day."
        },
        {
          type: "mc",
          q: "Your schedule: 'I ___ work at 9am and ___ at 5pm.'",
          options: ["start, finish", "starts, finishes", "starting, finishing"],
          a: "start, finish",
          tip: "I + base verb! Both actions = routine = present simple, no -s with 'I'."
        },
        {
          type: "match",
          q: "Match routine to time expression:",
          pairs: [
            { left: "I wake up early", right: "every day" },
            { left: "I work overtime", right: "sometimes" },
            { left: "I cook dinner", right: "usually" }
          ],
          a: "completed"
        },
        {
          type: "input",
          q: "Complete: 'We ___ lunch at 12:30pm in the break room.' (have)",
          a: "have"
        },
        {
          type: "mc",
          q: "'You ___ really hard!' (work)",
          options: ["work", "works", "working"],
          a: "work"
        },
        {
          type: "order",
          q: "Ordena: 'Ellos caminan al trabajo juntos'",
          words: ["They", "walk", "to", "work", "together."],
          a: "They walk to work together.",
          successtip: "✓ THEY WALK! Plural = base verb! 🚶‍♂️ No -s for we/they. Carpooling = Canadian eco-culture!"
        },
        {
          type: "input",
          q: "Complete: 'I ___ my emails first thing in the morning.' (check)",
          a: "check"
        },
        {
          type: "mc",
          q: "'We ___ overtime during busy season.'",
          options: ["work", "works", "are working"],
          a: "work",
          tip: "'During busy season' = repeated habit, NOT happening now. Use simple, not continuous!"
        }
      ]
    },

    // SET 2: Third Person -s (He/She/It)
    {
      id: "m4-u2-set2",
      title: "Set 2: Talking About Others",
      questions: [
        {
          type: "mc",
          q: "'My manager ___ the office at 8am sharp.' (arrive)",
          options: ["arrives", "arrive", "arriving"],
          a: "arrives",
          tip: "He/She/It + verb+S! 'Manager' = he/she = add -s. Critical rule!"
        },
        {
          type: "input",
          q: "Complete: 'She ___ at Tim Hortons every morning.' (stop)",
          a: "stops",
          successtip: "✓ STOPS! Third person -S! ☕ Tim Hortons = Canadian ritual! Coffee run before work!"
        },
        {
          type: "order",
          q: "Ordena: 'Mi hermano trabaja en construcción'",
          words: ["My", "brother", "works", "in", "construction."],
          a: "My brother works in construction."
        },
        {
          type: "mc",
          q: "ERROR: 'He work at the hospital' - Corrección:",
          options: ["He works", "He working", "He is work"],
          a: "He works",
          tip: "Classic error! He/She/It ALWAYS needs -s! 'He works' ✓"
        },
        {
          type: "match",
          q: "Match subject to correct verb form:",
          pairs: [
            { left: "I", right: "work" },
            { left: "She", right: "works" },
            { left: "They", right: "work" }
          ],
          a: "completed"
        },
        {
          type: "input",
          q: "Complete: 'My boss ___ the team meeting every Monday.' (lead)",
          a: "leads"
        },
        {
          type: "mc",
          q: "'Maria ___ two jobs to save money.' (have)",
          options: ["has", "have", "haves"],
          a: "has",
          successtip: "✓ HAS! Irregular! 💪 Two jobs = immigrant hustle! 'Have' becomes 'has' for he/she/it."
        },
        {
          type: "order",
          q: "Ordena: 'Él toma el metro al trabajo'",
          words: ["He", "takes", "the", "subway", "to", "work."],
          a: "He takes the subway to work.",
          tip: "'Subway' in Toronto/Montreal, 'SkyTrain' in Vancouver, 'C-Train' in Calgary!"
        },
        {
          type: "input",
          q: "Complete: 'The shift ___ at midnight.' (start)",
          a: "starts"
        },
        {
          type: "mc",
          q: "'My coworker always ___ late!' (arrive)",
          options: ["arrives", "arrive", "is arrive"],
          a: "arrives"
        }
      ]
    },

    // SET 3: Frequency Adverbs
    {
      id: "m4-u2-set3",
      title: "Set 3: How Often?",
      questions: [
        {
          type: "mc",
          q: "'I ___ work on weekends.' (100%)",
          options: ["always", "sometimes", "never"],
          a: "always",
          tip: "Always = 100%, usually = 80%, often = 60%, sometimes = 40%, rarely = 20%, never = 0%"
        },
        {
          type: "input",
          q: "Complete: 'She ___ checks her phone during work.' (constantly)",
          a: "constantly"
        },
        {
          type: "order",
          q: "Ordena: 'Yo generalmente como en casa'",
          words: ["I", "usually", "eat", "at", "home."],
          a: "I usually eat at home.",
          tip: "Frequency adverb BEFORE main verb! I usually eat ✓, I eat usually ✗"
        },
        {
          type: "match",
          q: "Match frequency to percentage:",
          pairs: [
            { left: "Always", right: "100%" },
            { left: "Sometimes", right: "40%" },
            { left: "Never", right: "0%" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "'How often do you work overtime?' - '___ twice a week.'",
          options: ["About", "At", "In"],
          a: "About",
          successtip: "✓ ABOUT! Approximation! 📊 'About twice' = approximately. Canadians use this a lot!"
        },
        {
          type: "input",
          q: "Complete: 'My manager ___ asks us to stay late.' (rarely)",
          a: "rarely"
        },
        {
          type: "order",
          q: "Ordena: 'Él nunca llega tarde'",
          words: ["He", "never", "arrives", "late."],
          a: "He never arrives late."
        },
        {
          type: "mc",
          q: "'I ___ go to the gym after work.' (50%)",
          options: ["sometimes", "always", "never"],
          a: "sometimes"
        },
        {
          type: "input",
          q: "Complete: 'We ___ have team meetings on Fridays.' (always)",
          a: "always"
        },
        {
          type: "mc",
          q: "Position: 'He is ___ on time.'",
          options: ["always", "always is", "is always"],
          a: "always",
          tip: "With 'to be': subject + BE + frequency + adjective. 'He is always on time' ✓"
        }
      ]
    },

    // SET 4: Work Schedules
    {
      id: "m4-u2-set4",
      title: "Set 4: Work & Schedules",
      questions: [
        {
          type: "mc",
          q: "'I ___ the morning shift at the warehouse.'",
          options: ["work", "works", "am work"],
          a: "work"
        },
        {
          type: "input",
          q: "Complete: 'She ___ from 9 to 5 Monday to Friday.' (work)",
          a: "works"
        },
        {
          type: "order",
          q: "Ordena: 'Nosotros tenemos un descanso de 30 minutos'",
          words: ["We", "have", "a", "30-minute", "break."],
          a: "We have a 30-minute break.",
          successtip: "✓ BREAK! Canadian labor law! ⏰ 30-minute break for shifts over 5 hours. Know your rights!"
        },
        {
          type: "match",
          q: "Match shift to schedule:",
          pairs: [
            { left: "Day shift", right: "8am - 4pm" },
            { left: "Evening shift", right: "4pm - 12am" },
            { left: "Night shift", right: "12am - 8am" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "'My company ___ flexible hours.'",
          options: ["offers", "offer", "offering"],
          a: "offers"
        },
        {
          type: "input",
          q: "Complete: 'He ___ overtime on weekends.' (work)",
          a: "works"
        },
        {
          type: "order",
          q: "Ordena: 'Ella tiene dos trabajos'",
          words: ["She", "has", "two", "jobs."],
          a: "She has two jobs."
        },
        {
          type: "mc",
          q: "'We ___ paid weekly.'",
          options: ["get", "gets", "getting"],
          a: "get"
        },
        {
          type: "input",
          q: "Complete: 'The factory ___ 24/7.' (operate)",
          a: "operates"
        },
        {
          type: "mc",
          q: "'I ___ benefits after three months.'",
          options: ["receive", "receives", "am receiving"],
          a: "receive",
          tip: "Benefits = health insurance, vacation, dental. Important job perk in Canada!"
        }
      ]
    },

    // SET 5: Negatives with DO/DON'T
    {
      id: "m4-u2-set5",
      title: "Set 5: What You Don't Do",
      questions: [
        {
          type: "mc",
          q: "'I ___ work on Sundays.'",
          options: ["don't", "doesn't", "am not"],
          a: "don't",
          tip: "I/You/We/They + DON'T + base verb. 'Don't' = do not."
        },
        {
          type: "input",
          q: "Complete: 'We ___ have meetings on Fridays.' (not have)",
          a: "don't have"
        },
        {
          type: "order",
          q: "Ordena: 'Ellos no trabajan turnos nocturnos'",
          words: ["They", "don't", "work", "night", "shifts."],
          a: "They don't work night shifts."
        },
        {
          type: "mc",
          q: "ERROR: 'I no work Sundays' - Corrección:",
          options: ["I don't work Sundays", "I not work Sundays", "I no working Sundays"],
          a: "I don't work Sundays",
          tip: "Spanish 'no trabajo' ≠ English! Need auxiliary: I DON'T work. Not just 'no'!"
        },
        {
          type: "match",
          q: "Match to correct negative:",
          pairs: [
            { left: "I", right: "don't work" },
            { left: "She", right: "doesn't work" },
            { left: "We", right: "don't work" }
          ],
          a: "completed"
        },
        {
          type: "input",
          q: "Complete: 'I ___ like working overtime.' (not like)",
          a: "don't like"
        },
        {
          type: "mc",
          q: "'You ___ need experience for this job.'",
          options: ["don't", "doesn't", "not"],
          a: "don't",
          successtip: "✓ ENTRY LEVEL! 🚪 No experience needed = great for newcomers! Start somewhere, grow anywhere!"
        },
        {
          type: "order",
          q: "Ordena: 'Nosotros no hablamos inglés en casa'",
          words: ["We", "don't", "speak", "English", "at", "home."],
          a: "We don't speak English at home."
        },
        {
          type: "input",
          q: "Complete: 'They ___ pay for parking.' (not pay)",
          a: "don't pay"
        },
        {
          type: "mc",
          q: "'I ___ have a car, I take transit.'",
          options: ["don't", "doesn't", "am not"],
          a: "don't"
        }
      ]
    },

    // SET 6: Negatives with DOESN'T
    {
      id: "m4-u2-set6",
      title: "Set 6: Third Person Negatives",
      questions: [
        {
          type: "mc",
          q: "'She ___ work Mondays.'",
          options: ["doesn't", "don't", "isn't"],
          a: "doesn't",
          tip: "He/She/It + DOESN'T + base verb (no -s on main verb!). 'Doesn't work' ✓"
        },
        {
          type: "input",
          q: "Complete: 'My boss ___ like excuses.' (not like)",
          a: "doesn't like"
        },
        {
          type: "order",
          q: "Ordena: 'Él no come carne'",
          words: ["He", "doesn't", "eat", "meat."],
          a: "He doesn't eat meat."
        },
        {
          type: "mc",
          q: "ERROR: 'She don't work here' - Corrección:",
          options: ["She doesn't work here", "She doesn't works here", "She not work here"],
          a: "She doesn't work here",
          tip: "Common error! She/He/It = DOESN'T (not 'don't'). And base verb after (work, not works)!"
        },
        {
          type: "match",
          q: "Match subject to negative:",
          pairs: [
            { left: "He doesn't", right: "like overtime" },
            { left: "I don't", right: "work weekends" },
            { left: "They don't", right: "have benefits" }
          ],
          a: "completed"
        },
        {
          type: "input",
          q: "Complete: 'The company ___ offer health insurance.' (not offer)",
          a: "doesn't offer"
        },
        {
          type: "mc",
          q: "'My coworker ___ speak English very well yet.'",
          options: ["doesn't", "don't", "isn't"],
          a: "doesn't"
        },
        {
          type: "order",
          q: "Ordena: 'El autobús no viene los domingos'",
          words: ["The", "bus", "doesn't", "come", "on", "Sundays."],
          a: "The bus doesn't come on Sundays.",
          successtip: "✓ TRANSIT SCHEDULE! 🚌 Sunday service = reduced in many Canadian cities. Plan ahead!"
        },
        {
          type: "input",
          q: "Complete: 'She ___ want to work nights.' (not want)",
          a: "doesn't want"
        },
        {
          type: "mc",
          q: "ERROR: 'He doesn't works' - ¿Por qué está mal?",
          options: [
            "After 'doesn't' use base verb (work), not 'works'",
            "Debe ser 'don't works'",
            "Falta el sujeto"
          ],
          a: "After 'doesn't' use base verb (work), not 'works'",
          tip: "Doesn't already shows third person! Don't add -s: doesn't work ✓, doesn't works ✗"
        }
      ]
    },

    // SET 7: Questions with DO/DOES
    {
      id: "m4-u2-set7",
      title: "Set 7: Asking About Routines",
      questions: [
        {
          type: "mc",
          q: "'___ you work full-time?'",
          options: ["Do", "Does", "Are"],
          a: "Do",
          tip: "Questions: DO + I/you/we/they + base verb?"
        },
        {
          type: "input",
          q: "Complete: '___ she work weekends?' (question)",
          a: "Does"
        },
        {
          type: "order",
          q: "Ordena: '¿A qué hora empiezas el trabajo?'",
          words: ["What", "time", "do", "you", "start", "work?"],
          a: "What time do you start work?"
        },
        {
          type: "match",
          q: "Match question to subject:",
          pairs: [
            { left: "Do you", right: "work here?" },
            { left: "Does he", right: "speak French?" },
            { left: "Do they", right: "have benefits?" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "'___ your company offer training?'",
          options: ["Does", "Do", "Is"],
          a: "Does",
          successtip: "✓ DOES! Company = it! 🏢 Asking about company benefits = smart interview question!"
        },
        {
          type: "input",
          q: "Complete: '___ they pay overtime?' (question)",
          a: "Do"
        },
        {
          type: "order",
          q: "Ordena: '¿Cuántas horas trabajas por semana?'",
          words: ["How", "many", "hours", "do", "you", "work", "per", "week?"],
          a: "How many hours do you work per week?"
        },
        {
          type: "mc",
          q: "ERROR: 'You work weekends?' - ¿Qué falta?",
          options: ["'Do' at the start", "'-s' in work", "Subject"],
          a: "'Do' at the start",
          tip: "Questions need auxiliary! 'Do you work weekends?' ✓ 'You work weekends?' = incomplete."
        },
        {
          type: "input",
          q: "Complete: 'Where ___ she work?' (question)",
          a: "does"
        },
        {
          type: "mc",
          q: "'___ he like his job?'",
          options: ["Does", "Do", "Is"],
          a: "Does"
        }
      ]
    },

    // SET 8: Present Simple vs Present Continuous
    {
      id: "m4-u2-set8",
      title: "Set 8: Simple vs Continuous",
      questions: [
        {
          type: "mc",
          q: "General truth: 'I ___ at a hospital.' (routine job)",
          options: ["work", "am working", "works"],
          a: "work",
          tip: "Permanent/regular = Simple. Temporary/now = Continuous. Job = usually simple!"
        },
        {
          type: "input",
          q: "Right now: 'She ___ on a report.' (present continuous)",
          a: "is working"
        },
        {
          type: "order",
          q: "Ordena (habitual): 'Yo tomo café todas las mañanas'",
          words: ["I", "drink", "coffee", "every", "morning."],
          a: "I drink coffee every morning.",
          tip: "'Every morning' = repeated habit = present simple!"
        },
        {
          type: "match",
          q: "Match situation to tense:",
          pairs: [
            { left: "I work here", right: "Permanent job (simple)" },
            { left: "I'm working now", right: "At this moment (continuous)" },
            { left: "He works Mondays", right: "Routine (simple)" }
          ],
          a: "completed",
          successtip: "✓ TENSE LOGIC! Key difference! 📚 Simple = always/usually. Continuous = now/temporary!"
        },
        {
          type: "mc",
          q: "'Usually I take the bus, but today I ___ my car.'",
          options: ["am driving", "drive", "drives"],
          a: "am driving",
          tip: "'Today' = exception to routine = continuous! 'Usually' = simple, 'today' = continuous."
        },
        {
          type: "input",
          q: "Routine: 'He ___ at 6am every day.' (wake up - simple)",
          a: "wakes up"
        },
        {
          type: "mc",
          q: "'Right now, we ___ a meeting.'",
          options: ["are having", "have", "has"],
          a: "are having"
        },
        {
          type: "order",
          q: "Ordena (en este momento): 'Ella está comiendo almuerzo ahora'",
          words: ["She", "is", "eating", "lunch", "now."],
          a: "She is eating lunch now."
        },
        {
          type: "input",
          q: "General: 'The store ___ at 9am.' (open - simple)",
          a: "opens"
        },
        {
          type: "mc",
          q: "ERROR: 'I am work at Walmart' - Corrección:",
          options: ["I work at Walmart", "I working at Walmart", "I am works at Walmart"],
          a: "I work at Walmart",
          tip: "Job = permanent = simple! 'I work' ✓. Don't use continuous for permanent jobs!"
        }
      ]
    },

    // SET 9: Time Expressions
    {
      id: "m4-u2-set9",
      title: "Set 9: When & How Often",
      questions: [
        {
          type: "match",
          q: "Match time expression to tense:",
          pairs: [
            { left: "every day", right: "Present simple" },
            { left: "right now", right: "Present continuous" },
            { left: "usually", right: "Present simple" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "'I check my email ___ I arrive at work.'",
          options: ["as soon as", "while", "during"],
          a: "as soon as",
          tip: "'As soon as' = immediately when. 'While' = during the same time. 'During' + noun."
        },
        {
          type: "input",
          q: "Complete: 'He works ___ Monday ___ Friday.' (from...to)",
          a: "from, to"
        },
        {
          type: "order",
          q: "Ordena: 'Trabajo de 9 a 5 todos los días'",
          words: ["I", "work", "from", "9", "to", "5", "every", "day."],
          a: "I work from 9 to 5 every day."
        },
        {
          type: "mc",
          q: "'___ do you work?' - 'I work Monday to Friday.'",
          options: ["When", "What", "Where"],
          a: "When"
        },
        {
          type: "input",
          q: "Complete: 'She takes a break ___ noon.' (at)",
          a: "at",
          successtip: "✓ AT NOON! Time preposition! ⏰ AT + specific time (at 9am, at noon, at midnight)!"
        },
        {
          type: "mc",
          q: "'I work overtime ___ the holiday season.'",
          options: ["during", "in", "at"],
          a: "during"
        },
        {
          type: "order",
          q: "Ordena: '¿Con qué frecuencia trabajas turnos nocturnos?'",
          words: ["How", "often", "do", "you", "work", "night", "shifts?"],
          a: "How often do you work night shifts?"
        },
        {
          type: "input",
          q: "Complete: 'We have lunch ___ 12:30.' (at/in/on)",
          a: "at"
        },
        {
          type: "mc",
          q: "'I get paid ___ two weeks.'",
          options: ["every", "each", "all"],
          a: "every",
          tip: "'Every two weeks' = bi-weekly pay. Common in Canada!"
        }
      ]
    },

    // SET 10: Real Workplace Scenarios
    {
      id: "m4-u2-set10",
      title: "Set 10: Work Culture",
      questions: [
        {
          type: "mc",
          q: "'In Canada, most people ___ for 8 hours a day.'",
          options: ["work", "works", "are working"],
          a: "work"
        },
        {
          type: "input",
          q: "Complete: 'My company ___ remote work options.' (offer)",
          a: "offers",
          successtip: "✓ REMOTE WORK! Modern benefit! 💻 Work from home = huge perk post-pandemic! Ask about it!"
        },
        {
          type: "order",
          q: "Ordena: 'Muchos inmigrantes trabajan en servicio al cliente'",
          words: ["Many", "immigrants", "work", "in", "customer", "service."],
          a: "Many immigrants work in customer service."
        },
        {
          type: "match",
          q: "Match Canadian workplace norm:",
          pairs: [
            { left: "Punctuality", right: "Arrive on time, always" },
            { left: "Breaks", right: "Take your full break time" },
            { left: "Overtime", right: "Usually gets paid 1.5x" }
          ],
          a: "completed",
          tip: "Canadian work culture: punctuality = respect. Breaks = your right. Overtime = usually paid!"
        },
        {
          type: "mc",
          q: "'Most Canadians ___ coffee at work.'",
          options: ["drink", "drinks", "are drinking"],
          a: "drink"
        },
        {
          type: "input",
          q: "Complete: 'He ___ commute an hour each way.' (have to)",
          a: "has to"
        },
        {
          type: "mc",
          q: "'Many companies ___ health benefits after probation.'",
          options: ["provide", "provides", "providing"],
          a: "provide"
        },
        {
          type: "order",
          q: "Ordena: 'Ella ahorra dinero para traer a su familia'",
          words: ["She", "saves", "money", "to", "bring", "her", "family."],
          a: "She saves money to bring her family.",
          successtip: "✓ FAMILY REUNIFICATION! 👨‍👩‍👧‍👦 Every immigrant's dream! Work hard, save, sponsor. You'll get there!"
        },
        {
          type: "input",
          q: "Complete: 'We ___ team building activities monthly.' (do)",
          a: "do"
        },
        {
          type: "mc",
          q: "'I ___ my paycheck to my family back home.'",
          options: ["send", "sends", "am sending"],
          a: "send",
          successtip: "✓ REMITTANCES! Global reality! 💰 Sending money home = supporting family. Western Union, Wise, remitly = tools!"
        }
      ]
    }
  ],

  // ===================================================================
// READING SET: Diego's Daily Routine
// ===================================================================
readingSet: {
  id: "m4-u2-reading",
  title: "Set 11: Reading Practice",
  category: "reading",

  questions: [
    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 1: Morning Routine & Commute (preguntas 1-2)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Morning Routine**

My name is Diego and I work at a warehouse in Mississauga. I start work at 7am every morning, so I wake up at 5:30am. I don't have a car, so I take the bus to work. It takes about 40 minutes.`
    },
    {
      type: "mc",
      q: "¿A qué hora empieza Diego el trabajo?",
      options: ["7am", "5:30am", "3:30pm"],
      a: "7am",
      successtip: "✓ READING DETAIL!\nHe starts at 7:00 AM. He wakes up at 5:30 AM to be on time! ⏰"
    },
    {
      type: "mc",
      q: "¿Cómo va Diego al trabajo?",
      options: ["By bus", "By car", "He walks"],
      a: "By bus",
      successtip: "✓ TRANSPORT!\nHe doesn't have a car, so he takes the bus. 🚌"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 2: At the Warehouse (preguntas 3, 4, 6)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Work Schedule**

I usually eat breakfast at work during my first break. We have two 15-minute breaks and one 30-minute lunch break. I work from Monday to Friday, and sometimes I work overtime on Saturdays. I finish at 3:30pm.`
    },
    {
      type: "mc",
      q: "¿Cuándo come Diego el desayuno?",
      options: ["During his first break at work", "At home at 5:30am", "During lunch"],
      a: "During his first break at work",
      successtip: "✓ BREAK TIME!\nHe eats breakfast during his first 15-minute break. 🥪"
    },
    {
      type: "mc",
      q: "¿Cuántos días trabaja Diego normalmente?",
      options: ["5 days (Monday-Friday)", "6 days (with Saturday)", "7 days"],
      a: "5 days (Monday-Friday)",
      tip: "He works 'sometimes' on Saturdays, meaning it's not his regular schedule. 📅",
      successtip: "✓ NORMAL SCHEDULE!\nHis official schedule is Monday to Friday. 💼"
    },
    {
      type: "mc",
      q: "¿Cuántos descansos tiene Diego durante su turno?",
      options: ["Three (two 15-min, one 30-min)", "Two breaks", "One lunch break"],
      a: "Three (two 15-min, one 30-min)",
      successtip: "✓ TOTAL BREAKS!\nTwo short breaks + one lunch break = 3 breaks in total. 🕒"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 3: Life, Goals & Grammar (preguntas 5, 7, 8, 9, 10)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Family and Future**

After work, I go to English class twice a week. On weekends, I send money to my family in Guatemala. I don't have much free time, but I'm saving money to bring my wife and kids here next year.`
    },
    {
      type: "mc",
      q: "¿Qué hace Diego después del trabajo dos veces por semana?",
      options: ["English class", "Overtime", "Sends money"],
      a: "English class",
      successtip: "✓ ESL STUDENT!\nWorking full-time and studying English is impressive dedication! 📚💪"
    },
    {
      type: "mc",
      q: "¿Qué hace Diego los fines de semana?",
      options: ["Sends money to his family", "Works overtime", "Studies English"],
      a: "Sends money to his family",
      successtip: "✓ FAMILY FIRST!\nHe supports his family in Guatemala. 🇬🇹💸"
    },
    {
      type: "mc",
      q: "¿Cuál es el objetivo principal de Diego?",
      options: ["Bring his wife and kids to Canada", "Buy a car", "Change jobs"],
      a: "Bring his wife and kids to Canada",
      successtip: "✓ BIG GOAL!\nHe is working hard to reunite with his family. 👨‍👩‍👧‍👦🍁"
    },
    {
      type: "mc",
      q: "El texto usa principalmente el tiempo verbal:",
      options: ["Present simple", "Present continuous", "Past simple"],
      a: "Present simple",
      tip: "Routines and habits use the Present Simple! 'I work', 'I wake up', 'I take'... 📖",
      successtip: "✓ GRAMMAR MASTER!\nYou identified the tense used for daily routines. 🎯"
    },
    {
      type: "mc",
      q: "Inferencia: ¿Por qué Diego no tiene mucho tiempo libre?",
      options: [
        "Porque trabaja tiempo completo, estudia inglés y trabaja horas extras",
        "Porque tiene carro",
        "Porque vive lejos"
      ],
      a: "Porque trabaja tiempo completo, estudia inglés y trabaja horas extras",
      successtip: "✓ IMMIGRANT GRIND!\nIt's a typical sacrifice to build a better future. Keep going! 🚀"
    }
  ]
},
  // REVIEW SET
  reviewSet: {
    id: "m4-u2-review",
    title: "Set 12: Unit Review",
    category: "review",
    questions: [
      {
        type: "mc",
        q: "Present simple se usa para:",
        options: [
          "Rutinas y hábitos",
          "Acciones en este momento",
          "Acciones pasadas"
        ],
        a: "Rutinas y hábitos"
      },
      {
        type: "match",
        q: "Match subject to verb form:",
        pairs: [
          { left: "I work", right: "Base verb" },
          { left: "She works", right: "Verb + s" },
          { left: "They work", right: "Base verb" }
        ],
        a: "completed"
      },
      {
        type: "mc",
        q: "'He ___ at 6am every day.' (wake up)",
        options: ["wakes up", "wake up", "waking up"],
        a: "wakes up",
        tip: "He/She/It + verb+S! Third person = add -s."
      },
      {
        type: "input",
        q: "Negative: 'I ___ work on weekends.' (not work)",
        a: "don't work"
      },
      {
        type: "mc",
        q: "'She ___ speak English fluently yet.'",
        options: ["doesn't", "don't", "isn't"],
        a: "doesn't"
      },
      {
        type: "order",
        q: "Question: '¿Trabajas tiempo completo?'",
        words: ["Do", "you", "work", "full-time?"],
        a: "Do you work full-time?"
      },
      {
        type: "mc",
        q: "Frequency adverb position: 'He ___ late.'",
        options: ["is always", "always is", "is late always"],
        a: "is always",
        tip: "With 'to be': subject + BE + frequency + adjective."
      },
      {
        type: "input",
        q: "Complete: 'Where ___ she work?' (question)",
        a: "does"
      },
      {
        type: "mc",
        q: "'Usually I walk, but today I ___ the bus.'",
        options: ["am taking", "take", "takes"],
        a: "am taking",
        tip: "'Today' = now, exception = continuous! 'Usually' = simple, 'today' = continuous."
      },
      {
        type: "mc",
        q: "Complete integration: 'I ___ at a hospital, I ___ from 7am to 3pm, and I ___ Sundays off.'",
        options: ["work, work, have", "works, works, has", "am working, work, have"],
        a: "work, work, have",
        successtip: "✓ UNIT 2 COMPLETE! Routine mastery! 💼 You can now describe your schedule, talk about work, and explain your habits perfectly! From Diego's story to your story! 🎉🇨🇦"
      }
    ]
  }
};
